import { PublicShell } from "@/components/layout/PublicShell";
import { DeviceSession } from "@/components/session/DeviceSession";
import { EmptyState } from "@/components/layout/AppShell";
import { useLocation, useSearchParams } from "react-router-dom";
import { transformUrl } from "@/auth/auth";
import { scopeAllows, signedTokenScopes } from "@/lib/scopes";
import { AlertCircle } from "lucide-react";

type SignedStreamParams =
  | {
      url: string;
      token: string;
      showFrame: boolean;
    }
  | {
      error: string;
    };

function parseSignedStreamParams(
  searchParams: URLSearchParams,
  hash: string,
): SignedStreamParams {
  const rawUrl = searchParams.get("url")?.trim();
  // The token now arrives in the fragment, which never leaves the browser.
  // Links minted before that change carry it in the query, so read both for as
  // long as one of those can still be in someone's tab or history.
  const hashParams = new URLSearchParams(hash.replace(/^#/, ""));
  const token = (hashParams.get("token") ?? searchParams.get("token"))?.trim();
  const showFrame = searchParams.get("showFrame") !== "false";

  if (!rawUrl || !token) {
    return { error: "Signed stream requires both a url and a token." };
  }

  let parsedUrl: URL;
  try {
    parsedUrl = new URL(rawUrl);
  } catch {
    return { error: "Signed stream url must be a valid absolute WebSocket URL." };
  }

  if (parsedUrl.protocol !== "ws:" && parsedUrl.protocol !== "wss:") {
    return { error: "Signed stream url must start with ws:// or wss://." };
  }

  return {
    url: transformUrl(parsedUrl.toString()) ?? parsedUrl.toString(),
    token,
    showFrame,
  };
}

// deriveApiUrl recovers the instance's HTTP API base URL from its signaling
// WebSocket URL so the live capture panels can reach the runtime's SSE
// endpoints. Two shapes exist: the router status URL ends in
// /endpointWebSocket (the api endpoint is its sibling), and SDK-derived URLs
// append /ws (Android) or /signaling (iOS) to the api URL. Anything else
// returns undefined and the panels are simply not shown.
function deriveApiUrl(wsUrl: string): string | undefined {
  let parsed: URL;
  try {
    parsed = new URL(wsUrl);
  } catch {
    return undefined;
  }
  parsed.protocol = parsed.protocol === "wss:" ? "https:" : "http:";
  parsed.search = "";
  parsed.hash = "";
  const segments = parsed.pathname.replace(/\/+$/, "").split("/");
  const last = segments[segments.length - 1];
  if (last === "endpointWebSocket") {
    segments[segments.length - 1] = "api";
  } else if (last === "ws" || last === "signaling") {
    segments.pop();
  } else {
    return undefined;
  }
  parsed.pathname = segments.join("/");
  return parsed.toString().replace(/\/+$/, "");
}

// Android exposes ADB and tunnel inspection through a sibling of the signaling route.
function deriveAdbWebSocketUrl(wsUrl: string): string | undefined {
  let parsed: URL;
  try {
    parsed = new URL(wsUrl);
  } catch {
    return undefined;
  }
  parsed.search = "";
  parsed.hash = "";
  const segments = parsed.pathname.replace(/\/+$/, "").split("/");
  if (segments[segments.length - 1] !== "endpointWebSocket") {
    return undefined;
  }
  segments[segments.length - 1] = "adbWebSocket";
  parsed.pathname = segments.join("/");
  return parsed.toString().replace(/\/+$/, "");
}

// instanceIdFromWsUrl extracts the instance id from the signaling URL. Its
// position depends on the URL shape — iOS mints /v1/{id}/endpointWebSocket
// while Android still mints the long legacy form
// /v1/organizations/{org}/android.limrun.com/v1/instances/{id}/endpointWebSocket —
// so scan for the TID instead of assuming a segment index.
function instanceIdFromWsUrl(wsUrl: string): string | undefined {
  try {
    return new URL(wsUrl).pathname
      .split("/")
      .find((segment) => /^(ios|android)_/.test(segment));
  } catch {
    return undefined;
  }
}

export default function SignedStreamPage() {
  const [searchParams] = useSearchParams();
  const { hash } = useLocation();
  const parsed = parseSignedStreamParams(searchParams, hash);
  if ("error" in parsed) {
    return <PublicShell><EmptyState icon={<AlertCircle />} title="Invalid stream link" description={parsed.error} /></PublicShell>;
  }
  const instanceId = instanceIdFromWsUrl(parsed.url);
  const platform = instanceId?.startsWith("ios_") ? "ios" : instanceId?.startsWith("android_") ? "android" : undefined;
  const scopes = signedTokenScopes(parsed.token);
  // Token claims only decide which controls to show. The router and runtime enforce access.
  const canUseTools = !!platform && !!instanceId && (!scopes || scopeAllows(scopes, platform, instanceId, "all"));
  const canStream = !scopes || !platform || !instanceId || scopeAllows(scopes, platform, instanceId, "stream");
  const apiUrl = deriveApiUrl(parsed.url);
  return (
    <PublicShell centered={false} viewport>
      <DeviceSession
        title={platform === "ios" ? "iOS device" : platform === "android" ? "Android device" : "Shared device"}
        status="Shared session"
        ready={canStream}
        canUseTools={canUseTools}
        showFrame={parsed.showFrame}
        isDuo={platform === "ios" && searchParams.get("model") === "iphone-duo"}
        connection={canStream ? {
          instanceId, platform, url: parsed.url, token: parsed.token, apiUrl,
          adbWebSocketUrl: platform === "android" ? deriveAdbWebSocketUrl(parsed.url) : undefined,
        } : undefined}
        fallback={!canStream ? <EmptyState icon={<AlertCircle />} title="Streaming unavailable"
          description="This link does not grant streaming access to this device." /> : undefined}
      />
    </PublicShell>
  );
}
